import * as Api from '@ViewModels';
import { css, StyleDeclarationValue } from 'aphrodite';
import { useMediaQuery } from 'react-responsive';
import {
	ICreateCampaignRequest,
	ICreateSocialMediaPostRequest,
	ILocationState,
	TemplateOrTemplateFilter,
} from '../../../../models';
import { useFullscreenModal, useUserSession } from '../../../../models/hooks/appStateHooks';
import { getDisplayName } from '../../../../models/NameUtils';
import { getShortenedString } from '../../../../models/UiUtils';
import { ITemplateCard } from '../../../../viewmodels/AppViewModels';
import { Button } from '../../../components-v2/Button';
import { ButtonGroup } from '../../../components-v2/ButtonGroup';
import {
	Dropdown,
	DropdownContent,
	DropdownEllipsisTrigger,
	DropdownItem,
	DropdownTrigger,
} from '../../../components-v2/Dropdown';
import { baseStyleSheet } from '../../../styles/styles';
import { ChevronDown } from '../../svgs/icons/ChevronDown';
import { getTemplateApprovalStatus, getTemplateButtonTextOptions } from '../../TemplateReviewStatusIndicator';
import { SimpleTooltip } from '../../Tooltip';
import { s as tooltipStyles } from '../../Tooltip/styles';
import { styleSheet } from './styles';

export const CampaignTemplateCardTruncatedBody = ({
	style,
	showReadMore = true,
	text,
	truncated,
	fullText,
	useTwoLineDisplay = false,
}: {
	style?: StyleDeclarationValue[];
	showReadMore?: boolean;
	text: string;
	truncated: boolean;
	fullText?: string;
	useTwoLineDisplay?: boolean;
}) => {
	const contentElement = (
		<p className={css(styleSheet.textContent, useTwoLineDisplay && styleSheet.textContentTwoLines, ...(style || []))}>
			<span>
				{text}
				{truncated && !useTwoLineDisplay ? '... ' : ''}
			</span>
			{truncated && showReadMore && !useTwoLineDisplay ? (
				<strong className={css(baseStyleSheet.fontBold)} dangerouslySetInnerHTML={{ __html: 'Read more' }} />
			) : null}
		</p>
	);

	// If we have full text and are using two-line display, wrap with tooltip
	if (useTwoLineDisplay && fullText && fullText !== text) {
		return (
			<SimpleTooltip title={fullText} styles={[tooltipStyles.contentWithMaxWidth]}>
				{contentElement}
			</SimpleTooltip>
		);
	}

	return contentElement;
};

export const CampaignTemplateCardCta = ({
	ctaText,
	finraImage,
	isFinraReviewed,
	card,
	templateType,
	onCtaClick,
}: {
	ctaText?: string;
	finraImage?: string;
	isFinraReviewed?: boolean;
	card?: ITemplateCard;
	templateType: Api.TemplateType;
	onCtaClick?(): void;
}) => {
	const fullscreenModal = useFullscreenModal();
	const userSession = useUserSession();

	const buttonText = getTemplateButtonTextOptions(userSession);
	const approvalStatus = getTemplateApprovalStatus(card?.templateReview);

	const hasOptions = buttonText.preview && buttonText.review;

	const handlePrimaryClick = async () => {
		if (buttonText.review) {
			const templates = new Api.TemplatesViewModel(userSession);
			const fetchedTemplate = await templates.getByIdExpandedByLastUsedBy(card.id);

			if (templateType === Api.TemplateType.SocialMediaPost) {
				const locationState: ILocationState<any, ICreateSocialMediaPostRequest<Api.ITemplate>> = {
					model: {
						context: { ...fetchedTemplate },
						type: 'Template',
					},
				};
				fullscreenModal?.history.push({
					pathname: '/social-media/post/review/from-template',
					state: locationState,
				});
			} else if (templateType === Api.TemplateType.Email) {
				const locationState: ILocationState<any, ICreateCampaignRequest<TemplateOrTemplateFilter>> = {
					model: {
						context: { ...fetchedTemplate },
						type: 'Template',
					},
				};
				fullscreenModal?.history.push({
					pathname: '/email/campaigns/review',
					state: locationState,
				});
			}
		} else {
			handleOptionClick();
		}
	};

	const handleOptionClick = async () => {
		if (onCtaClick) {
			onCtaClick();
			return;
		}

		const templates = new Api.TemplatesViewModel(userSession);
		const fetchedTemplate = await templates.getByIdExpandedByLastUsedBy(card.id);

		if (templateType === Api.TemplateType.SocialMediaPost) {
			const locationState: ILocationState<any, ICreateSocialMediaPostRequest<Api.ITemplate>> = {
				model: {
					context: { ...fetchedTemplate },
					type: 'Template',
				},
			};
			fullscreenModal?.history.push({
				pathname: '/social-media/post/create/from-template',
				state: locationState,
			});
		} else if (templateType === Api.TemplateType.Email) {
			const locationState: ILocationState<any, ICreateCampaignRequest<TemplateOrTemplateFilter>> = {
				model: {
					context: { ...fetchedTemplate },
					type: 'Template',
				},
			};
			fullscreenModal?.history.push({
				pathname: '/email/campaigns/create/from-template',
				state: locationState,
			});
		}
	};

	return (
		<div className={css(baseStyleSheet.horizontalStack, styleSheet.buttonStack)}>
			<ButtonGroup>
				{!hasOptions ? (
					<Button variant='secondary' size='sm' onClick={() => handleOptionClick()}>
						{ctaText || buttonText?.preview}
					</Button>
				) : null}
				{hasOptions ? (
					<>
						<Button variant='secondary' size='sm' onClick={handlePrimaryClick}>
							{ctaText || buttonText?.review}
						</Button>
						<Dropdown>
							<DropdownTrigger render={<Button variant='secondary' size='sm' />}>
								<ChevronDown />
							</DropdownTrigger>
							<DropdownContent align='end'>
								<DropdownItem onClick={() => handleOptionClick()}>{buttonText?.preview}</DropdownItem>
							</DropdownContent>
						</Dropdown>
					</>
				) : null}
			</ButtonGroup>
			{isFinraReviewed ? (
				<figure className={css(styleSheet.reviewedStack)}>
					<img src={finraImage} height={14} />
					<span className={css(styleSheet.reviewed)}> Reviewed</span>
				</figure>
			) : null}
			{!isFinraReviewed ? approvalStatus : null}
		</div>
	);
};

export const CampaignTemplateCardMenu = ({
	card,
	onDelete,
	onShare,
	userSession,
}: {
	card: ITemplateCard;
	onDelete?: () => void;
	onShare?: (val: boolean) => void;
	userSession: Api.UserSessionContext;
}) => {
	const canShare =
		onShare && (card.creator?.id === userSession?.user?.id || userSession?.user?.role?.toLowerCase().includes('admin'));

	return (
		<div className={css(styleSheet.menu)}>
			<Dropdown>
				<DropdownEllipsisTrigger />
				<DropdownContent>
					<DropdownItem onClick={() => onDelete?.()}>Delete</DropdownItem>
					{canShare && card?.scope === Api.TemplateScope.Account ? (
						<DropdownItem onClick={() => onShare?.(false)}>Unshare</DropdownItem>
					) : null}
					{canShare && card?.scope !== Api.TemplateScope.Account ? (
						<DropdownItem onClick={() => onShare?.(true)}>Share</DropdownItem>
					) : null}
					{!canShare || (canShare && card?.scope === Api.TemplateScope.Account) ? (
						<DropdownItem disabled>
							Shared by{' '}
							{card.creator
								? card.creator?.id === userSession?.user?.id
									? 'You'
									: getDisplayName(card.creator, true)
								: 'unknown'}
						</DropdownItem>
					) : null}
				</DropdownContent>
			</Dropdown>
		</div>
	);
};

export const CampaignSummaryFromContentShort = ({ card }: { card: ITemplateCard }) => {
	const isLargeScreen = useMediaQuery({ minWidth: 1200 });
	const maxCharCount = isLargeScreen ? 120 : 100;
	const summary = card?.summary;
	const shortSummary = summary ? getShortenedString(summary?.trim(), maxCharCount) : '';
	return (
		<div className={css(styleSheet.textContainer, styleSheet.textContainerSmall)}>
			<CampaignTemplateCardTruncatedBody
				style={[styleSheet.textContentSmall]}
				text={shortSummary}
				truncated={summary?.length >= maxCharCount}
				showReadMore={false}
			/>
		</div>
	);
};

const htmlCache = new Map();
function getParsedHTML(html: string) {
	if (!htmlCache.has(html)) {
		const dom = new DOMParser().parseFromString(html, 'text/html').body.firstChild;
		htmlCache.set(html, dom);
	}
	return htmlCache.get(html) as HTMLElement;
}

export const CampaignSummaryFromContent = ({ card }: { card: ITemplateCard }) => {
	const parsedHTML = getParsedHTML(card?.content?.document);

	return <div className={css(styleSheet.summary)} dangerouslySetInnerHTML={{ __html: parsedHTML.outerHTML }} />;
};
